import { RefinementCtx } from 'zod';
import { OrganType } from '@/api/apis/case/clinical-data.types';
import { EmployeeRole } from '@/api/apis/resources/resources.types';
import { EmployeeFormType, NameByValue } from '@/types';
import { COUNTRY_SCHEMA, getPhoneSchema, addCustomZodErrors } from '@/utils/helpers';
import { getCountryHasStates } from '@/hooks/useCountrySubdivisionInfo';

export const createEmployeeFormValidation =
  (composedSubdivisionsList: Record<string, NameByValue[]>) =>
  (
    {
      roles,
      organType,
      liverSurgeonRegionId,
      heartLungsSurgeonRegionId,
      clinicalSpecialistShiftGroupType,
      shiftTimeGroup,
      clinicalCoordinatorType,
      country,
      state,
      scrubSizeTop,
      scrubSizeBottom,
      weight,
      locationPreference,
      baseLocationId,
      phoneSupportOrganSpecializations,
      tsaPrecheckNumber,
      phone,
      rsScheduleManagerOrgans,
      organManagementSpecialistSpecializations,
    }: EmployeeFormType,
    ctx: RefinementCtx,
  ) => {
    const countryHasStates = getCountryHasStates(composedSubdivisionsList, country);
    const isStateValidation = countryHasStates && !state;

    const isLiverSurgeonRegionValidation =
      roles.includes(EmployeeRole.RetrievalSurgeon) && organType.includes(OrganType.Liver) && !liverSurgeonRegionId;

    const isheartLungsSurgeonRegionValidation =
      roles.includes(EmployeeRole.RetrievalSurgeon) &&
      (organType.includes(OrganType.Heart) || organType.includes(OrganType.Lungs)) &&
      !heartLungsSurgeonRegionId;

    const isClinicalSpecialistShiftGroupTypeValidation =
      roles.includes(EmployeeRole.ClinicalSpecialist) && !clinicalSpecialistShiftGroupType;

    const isShiftTimeGroupValidation = roles.includes(EmployeeRole.ClinicalSpecialist) && !shiftTimeGroup;

    const isClinicalCoordinatorTypeValidation =
      roles.includes(EmployeeRole.ClinicalCoordinator) && !clinicalCoordinatorType;

    const isUniformMeasurementsValidation =
      roles.includes(EmployeeRole.ClinicalSpecialist) || roles.includes(EmployeeRole.RetrievalSurgeon);

    const rolesForOrganValidation = [
      EmployeeRole.ClinicalAuditor,
      EmployeeRole.ClinicalSpecialist,
      EmployeeRole.RetrievalSurgeon,
      EmployeeRole.OrganLeadership,
      EmployeeRole.OrganManagement,
    ];

    const isOrganValidation = roles.some((role) => rolesForOrganValidation.includes(role)) && !organType.length;

    const isLocationPreferencesValidation =
      (roles.includes(EmployeeRole.ClinicalSpecialist) ||
        roles.includes(EmployeeRole.RetrievalSurgeon) ||
        roles.includes(EmployeeRole.ClinicalLead)) &&
      !locationPreference;

    const isBaseLocationValidation =
      (roles.includes(EmployeeRole.ClinicalSpecialist) ||
        roles.includes(EmployeeRole.RetrievalSurgeon) ||
        roles.includes(EmployeeRole.ClinicalLead)) &&
      !baseLocationId;

    const isClinicalLeadOrganValidation =
      roles.includes(EmployeeRole.ClinicalLead) && !phoneSupportOrganSpecializations.length;

    const isScheduleManagerRsValidation =
      roles.includes(EmployeeRole.ScheduleManagerRs) && !rsScheduleManagerOrgans.length;

    const isOrganManagementSpecialistSpecializations =
      roles.includes(EmployeeRole.OrganManagementSpecialist) && !organManagementSpecialistSpecializations.length;

    const isTsaPrecheckNumberValidation = tsaPrecheckNumber !== null && tsaPrecheckNumber?.trim() !== '';

    const phoneSchemaResult = getPhoneSchema({ isRequired: true }).safeParse(phone);

    const countrySchemaResult = COUNTRY_SCHEMA.safeParse(country);

    if (isLiverSurgeonRegionValidation) {
      addCustomZodErrors({
        ctx,
        path: ['liverSurgeonRegionId'],
        message: 'Liver region ID is required for Retrieval Surgeon',
      });
    }

    if (isheartLungsSurgeonRegionValidation) {
      addCustomZodErrors({
        ctx,
        path: ['heartLungsSurgeonRegionId'],
        message: 'Heart/Lungs region ID is required for Retrieval Surgeon',
      });
    }

    if (isClinicalSpecialistShiftGroupTypeValidation) {
      addCustomZodErrors({
        ctx,
        path: ['clinicalSpecialistShiftGroupType'],
        message: 'Provide shift group type',
      });
    }

    if (isShiftTimeGroupValidation) {
      addCustomZodErrors({
        ctx,
        path: ['shiftTimeGroup'],
        message: 'Provide shift time group',
      });
    }

    if (isClinicalCoordinatorTypeValidation) {
      addCustomZodErrors({
        ctx,
        path: ['clinicalCoordinatorType'],
        message: 'Provide TC level',
      });
    }

    if (!countrySchemaResult.success) {
      addCustomZodErrors({
        ctx,
        path: ['country'],
        message: countrySchemaResult.error.issues[0].message,
      });
    }

    if (isStateValidation) {
      addCustomZodErrors({
        ctx,
        path: ['state'],
        message: 'Select state',
      });
    }

    if (isOrganValidation) {
      addCustomZodErrors({
        ctx,
        path: ['organType'],
        message: 'Select organ',
      });
    }

    if (isUniformMeasurementsValidation) {
      if (!scrubSizeTop) {
        addCustomZodErrors({
          ctx,
          path: ['scrubSizeTop'],
          message: 'Select size top',
        });
      }

      if (!scrubSizeBottom) {
        addCustomZodErrors({
          ctx,
          path: ['scrubSizeBottom'],
          message: 'Select size bottom',
        });
      }

      if (!weight) {
        addCustomZodErrors({
          ctx,
          path: ['weight'],
          message: 'Provide weight',
        });
      }
    }

    if (isLocationPreferencesValidation) {
      addCustomZodErrors({
        ctx,
        path: ['locationPreference'],
        message: 'Select location',
      });
    }

    if (isBaseLocationValidation) {
      addCustomZodErrors({
        ctx,
        path: ['baseLocationId'],
        message: 'Select base location',
      });
    }

    if (isClinicalLeadOrganValidation) {
      addCustomZodErrors({
        ctx,
        path: ['phoneSupportOrganSpecializations'],
        message: 'Select organ',
      });
    }

    if (!phoneSchemaResult.success) {
      addCustomZodErrors({
        ctx,
        path: ['phone'],
        message: phoneSchemaResult.error.issues[0].message,
      });
    }

    if (isTsaPrecheckNumberValidation) {
      const validFormat = /^[a-zA-Z0-9]+$/.test(tsaPrecheckNumber);
      const validLength = tsaPrecheckNumber.length >= 9 && tsaPrecheckNumber.length <= 10;
      const validPrefix = /^(TT|TE|AC)/.test(tsaPrecheckNumber);

      if (!validFormat || !validLength || !validPrefix) {
        addCustomZodErrors({
          ctx,
          path: ['tsaPrecheckNumber'],
          message: 'TSA PreCheck number must be 9-10 alphanumeric characters and begin with TT, TE, or AC',
        });
      }
    }

    if (isScheduleManagerRsValidation) {
      addCustomZodErrors({
        ctx,
        path: ['rsScheduleManagerOrgans'],
        message: 'Select organ',
      });
    }
    if (isOrganManagementSpecialistSpecializations) {
      addCustomZodErrors({
        ctx,
        path: ['organManagementSpecialistSpecializations'],
        message: 'Select organ',
      });
    }
  };
